import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, List, Table, Checkbox, Button, Input, message, Tag } from 'antd';
import { SaveOutlined, SearchOutlined } from '@ant-design/icons';
import { useFinanceApi } from '../api/financeApi';
import { toplamGenislik } from '../../../components/ortak/tableHelpers';
import { trIncludes } from '../../../utils/trText';

const MODULE_LABELS = {
  odemeler: 'Ödemeler', tahsilatlar: 'Tahsilatlar', duzenli_odemeler: 'Düzenli Ödemeler',
  odeme_talebi: 'Ödeme Talebi', cari: 'Cari', islem_turu: 'İşlem Türleri', firma: 'Firmalar',
  banka_hesap: 'Banka Hesapları', fatura_eslestirme: 'Fatura Eşleştirme', krediler: 'Krediler',
  cekler: 'Çekler', hakedis: 'Hakediş', yetkiler: 'Yetkiler',
};
const ACTION_LABELS = { view: 'Görüntüle', create: 'Ekle', edit: 'Düzenle', delete: 'Sil' };

export default function YetkilerTab({ perms }) {
  const api = useFinanceApi();
  const p = perms.yetkiler || {};
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState(['view', 'create', 'edit', 'delete']);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [userPerms, setUserPerms] = useState({});
  const [saving, setSaving] = useState(false);

  const loadUsers = useCallback(async () => {
    if (p.view === false) return;
    try {
      const res = await api.get('/permissions/users');
      setUsers(res.users || []);
      setModules(res.modules || []);
      setActions(res.actions || ['view', 'create', 'edit', 'delete']);
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Kullanıcılar yüklenemedi');
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  if (p.view === false) return <div style={{ padding: 24 }}>Görüntüleme yetkiniz yok.</div>;

  const selectUser = async (u) => {
    setSelected(u);
    try {
      const res = await api.get(`/permissions/${u.id}`);
      setUserPerms(res.permissions || {});
    } catch {
      setUserPerms({});
    }
  };

  // unspecified => allowed (legacy default açık)
  const val = (mod, act) => {
    const m = userPerms[mod];
    if (!m || m[act] === undefined) return act === 'sadece_kendi' ? false : true;
    return !!m[act];
  };

  const toggle = (mod, act, checked) => {
    setUserPerms((prev) => ({ ...prev, [mod]: { ...(prev[mod] || {}), [act]: checked ? 1 : 0 } }));
  };

  const save = async () => {
    if (!selected) return;
    // Materialize explicit values for every module/action so it is deterministic.
    const full = {};
    modules.forEach((mod) => {
      full[mod] = { sadece_kendi: val(mod, 'sadece_kendi') ? 1 : 0 };
      actions.forEach((act) => { full[mod][act] = val(mod, act) ? 1 : 0; });
    });
    setSaving(true);
    try {
      await api.post(`/permissions/${selected.id}`, { permissions: full });
      message.success('Yetkiler kaydedildi');
      loadUsers();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = users.filter(
    (u) => !search || trIncludes(u.ad_soyad, search) || trIncludes(u.kul_adi, search));

  const matrixCols = [
    { title: 'Modül', dataIndex: 'label', width: 170, fixed: 'left' },
    ...actions.map((act) => ({
      title: ACTION_LABELS[act] || act, key: act, align: 'center', width: 100,
      render: (_, r) => (
        <Checkbox checked={val(r.mod, act)} disabled={p.edit === false}
          onChange={(e) => toggle(r.mod, act, e.target.checked)} />
      ),
    })),
    {
      title: 'Sadece Kendi', key: 'sk', align: 'center', width: 120,
      render: (_, r) => (
        <Checkbox checked={val(r.mod, 'sadece_kendi')} disabled={p.edit === false}
          onChange={(e) => toggle(r.mod, 'sadece_kendi', e.target.checked)} />
      ),
    },
  ];
  const matrixData = modules.map((mod) => ({ key: mod, mod, label: MODULE_LABELS[mod] || mod }));

  return (
    <Row gutter={12}>
      <Col xs={24} lg={7}>
        <Card size="small" title="Kullanıcılar">
          <Input allowClear prefix={<SearchOutlined />} placeholder="Kullanıcı ara"
            value={search} onChange={(e) => setSearch(e.target.value)} style={{ marginBottom: 8 }} />
          <List
            size="small" dataSource={filteredUsers} style={{ maxHeight: 460, overflow: 'auto' }}
            renderItem={(u) => (
              <List.Item
                onClick={() => selectUser(u)}
                style={{ cursor: 'pointer', background: selected?.id === u.id ? 'rgba(24,144,255,0.12)' : undefined }}
              >
                <span>{u.ad_soyad || u.kul_adi}</span>
                {u.has_perms ? <Tag color="blue">özel</Tag> : <Tag>varsayılan</Tag>}
              </List.Item>
            )}
          />
        </Card>
      </Col>
      <Col xs={24} lg={17}>
        <Card
          size="small"
          title={selected ? `Yetkiler — ${selected.ad_soyad || selected.kul_adi}` : 'Bir kullanıcı seçin'}
          extra={selected && p.edit !== false && (
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>Kaydet</Button>
          )}
        >
          {selected ? (
            <Table rowKey="key" size="small" pagination={false} dataSource={matrixData}
              columns={matrixCols} scroll={{ x: toplamGenislik(matrixCols), y: 460 }} />
          ) : (
            <div style={{ padding: 24, color: '#888' }}>Soldan bir kullanıcı seçerek yetkilerini düzenleyin.</div>
          )}
        </Card>
      </Col>
    </Row>
  );
}
